import { t } from '@lingui/core/macro';
import {
  AgenticProcess,
  ContextEntitiesEnum,
  dataContext,
  isValidTag,
  isHubOnly,
  PageId,
  Plan,
  Project,
  RemoteWorkerSession,
  systemTools,
  TypeId,
  VFSPath,
} from '@sdk';
import { redirect } from 'react-router';
import { DockPointer } from '@src/navigation';
import { isProjectLandingPointer } from '@src/navigation/asset-doc-types';
import { ViewType } from '@src/types/ViewType';
import { clearDockLoadError } from './dock-load-error-store';
import { DockLoadError, handleDockLoadError } from './dock-load-error';
import { projectInDockWorkspace } from './workspace-scope';
import { wikiAuthorityForPage } from '@src/components/wiki/resolve-wiki';
import { loadAssetRoute } from './load-asset';
import { loadConversationRoute } from './load-conversation';
import { loadLensRoute } from './load-lens';
import { loadProject, loadProjectRoute } from './load-project';
import { loadProcess, loadProcessProject, ProcessLoadError } from './load-process';
import { loadShellRoute, processRouteCarry } from './load-shell';
import { loadTasksRoute } from './load-tasks';
import { processLoadErrorToDockError } from './process-load-error-resolution';
import { loadAgentStreamInboxRoute } from './load-agent-stream-inbox';

export interface DockLoaderContext {
  requestPath: string;
}

/**
 * Adopt the project pinned in a dock's scope filter into context: the URL-first
 * project write for any dock whose loader has no entity to derive a project
 * from (browse views, micro apps, lenses). `main-loader` runs it for every
 * scope-pinned dock before the tab is materialized; the per-view calls below
 * cover the unscoped restore. Without it, entering a project whose landing tab
 * is one of these views leaves `CurrentProjectTypeId` pointing at the PREVIOUS
 * project (the stuck-footer switch bug). Entity-backed docks keep their own
 * derivation only when the URL names no project (`loadAssetRoute`'s
 * `keepProject`): a project named here is final.
 * Best-effort — a missing project must not fail a browse landing.
 */
export async function adoptScopeProject(dock: DockPointer): Promise<void> {
  const projectId = dock.scopeProjectId;
  if (!projectId) {
    // Only an unscoped, context-neutral route restores browser memory. Explicit
    // global scopes and entity-owned routes must never inherit a default here.
    // A project of ANOTHER workspace is neither restored nor kept: a workspace
    // starts with no project, like a fresh install (`?workspace=`, the URL's).
    const inWorkspace = projectInDockWorkspace(dock);
    if (inWorkspace && dataContext.project && !inWorkspace(dataContext.project)) {
      await dataContext.setContextEntityTypeId(ContextEntitiesEnum.CurrentProjectTypeId, null);
    }
    if (!dock.scopeFilter && !dataContext.project && !isHubOnly()) {
      await dataContext.setupProject(inWorkspace ?? undefined);
    }
    return;
  }
  if (dataContext.project?.id === projectId) return;
  await loadProject(new TypeId(Project.type, projectId)).catch(() => {});
}

async function loadPlanRoute(pointer: string | undefined): Promise<void> {
  if (!pointer) return;
  const parsed = DockPointer.parsePlanPointer(pointer);
  if (!parsed) return;

  // Legacy `agentic_process-<id>/<path>` form (old bookmarks): resolve the abs
  // path and redirect to the canonical, process-independent `vfs` form so the
  // view only ever sees the new grammar and the link self-heals on first open.
  if (parsed.kind === 'legacy') {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(`/dock/plan/${DockPointer.forPlanByPath(parsed.filePath).pointer}`);
  }

  // typeid form: the plan must resolve as a real entity, else render a clear
  // "not found" page (vs. an infinite spinner). The DockLoadErrorView shows it.
  if (parsed.kind === 'typeid') {
    const plan = await Plan.getById(parsed.planTypeId.id).catch(() => null);
    if (!plan) {
      throw new DockLoadError(
        'plan_not_found',
        'hard',
        {
          action: 'render_error',
          title: t`Plan not found`,
          message: t`This plan no longer exists.`,
        },
        'plan',
      );
    }
    return;
  }

  // vfs form: addressed by path on a (local) compute node — no entity required,
  // the view reads the file directly. Nothing to pre-resolve; `VFSPath.parse`
  // validates shape and the view surfaces a clear error if the file is missing.
  VFSPath.parse(parsed.vfsValue);
}

async function loadAgenticProcessRoute(pointer: string | undefined): Promise<void> {
  if (!pointer) return;
  const processId = DockPointer.isAgenticProcessPointer(pointer)
    ? DockPointer.extractAgenticProcessId(pointer)
    : pointer;
  try {
    new TypeId(AgenticProcess.type, processId);
  } catch (error) {
    throw new DockLoadError(
      'malformed_session_pointer',
      'hard',
      {
        action: 'render_error',
        title: t`Session not found`,
        message: t`This session URL is malformed or unavailable.`,
      },
      'agentic_process',
      error,
    );
  }
  try {
    await loadProcess(processId);
  } catch (error) {
    if (error instanceof ProcessLoadError) {
      throw processLoadErrorToDockError(error, 'agentic_process');
    }
    throw error;
  }
}

/**
 * `/dock/session/<processId>` — the session view: the process into context and
 * its project, like any process-owned dock. Cache-first identity; no runtime work.
 */
async function loadSessionRoute(pointer: string | undefined): Promise<void> {
  const processTypeId = pointer ? new TypeId(AgenticProcess.type, pointer) : null;
  await dataContext.setContextEntityTypeId(ContextEntitiesEnum.CurrentProcessTypeId, processTypeId);
  if (!processTypeId || !pointer) return;
  await dataContext.setActiveEntityTypeId(processTypeId);
  const process =
    AgenticProcess.getByIdFromCache<AgenticProcess>(pointer) ??
    (await AgenticProcess.getById<AgenticProcess>(pointer).catch(() => null));
  if (!process) {
    await systemTools.resolveProjectContext(undefined, undefined);
    return;
  }
  // The same project phase as any process route; a session URL stays loadable
  // when the project cannot be recovered — its workdir decides, else Global.
  await loadProcessProject(process).catch(() => systemTools.resolveProjectContext(process.workdir ?? undefined, process));
}

/** Resolve the portal project so context is written URL-first, exactly as a
 *  PROJECT dock does. The article (if any) is resolved by the view from the
 *  pointer — it is a file path, not an entity, so there is nothing to load. */
async function loadHelpdeskRoute(pointer: string | undefined): Promise<void> {
  const { projectId } = DockPointer.parseHelpdeskPointer(pointer);
  if (!projectId) return; // portal root with no project — the view renders its own empty state
  try {
    await loadProject(new TypeId(Project.type, projectId));
  } catch (error) {
    throw new DockLoadError(
      'helpdesk_project_not_found',
      'hard',
      {
        action: 'render_error',
        title: t`Help desk unavailable`,
        message: t`The help desk files are missing. Open the help desk again to download them.`,
        retryable: true,
      },
      'helpdesk',
      error,
    );
  }
}

function loadLiveSessionRoute(pointer: string | undefined): void {
  if (!pointer) return;
  // Identity validation only — the view watches the session entity live (a
  // guest-side DRAFT row may exist locally before anything is on the hub), so
  // the loader stays fast (no WS/PTY-bound work; see CLAUDE.md loader rule).
  try {
    new TypeId(RemoteWorkerSession.type, pointer);
  } catch (error) {
    throw new DockLoadError(
      'malformed_session_pointer',
      'hard',
      {
        action: 'render_error',
        title: t`Live session not found`,
        message: t`This live-session URL is malformed or unavailable.`,
      },
      'live_session',
      error,
    );
  }
}

/**
 * Validate a graph root and adopt it as the URL-owned active entity. This is
 * intentionally the loader's entire responsibility: graph reads happen in the
 * mounted view and cloud inventory sync is only triggered by its Sync button.
 */
export async function loadGraphIdentityRoute(dock: DockPointer, surface: 'graph' | 'worldview'): Promise<void> {
  try {
    if (surface === 'worldview') {
      if (!DockPointer.parseWorldViewProjection(dock.pointer)) {
        throw new Error('Expected a WorldView projection');
      }
      const focus = dock.options?.focus;
      await dataContext.setActiveEntityTypeId(focus ? new TypeId(focus) : null);
      return;
    }

    if (!dock.pointer) return;
    const parsed = DockPointer.parseGraphPointer(dock.pointer);
    if (!parsed) throw new Error('Expected <type>/<id>');
    await dataContext.setActiveEntityTypeId(new TypeId(parsed.type, parsed.id));
  } catch (error) {
    throw new DockLoadError(
      'malformed_graph_pointer',
      'hard',
      {
        action: 'render_error',
        title: surface === 'worldview' ? 'WorldView not found' : 'Graph root not found',
        message: t`This ${surface === 'worldview' ? 'WorldView' : 'graph'} URL is malformed or unavailable.`,
      },
      surface,
      error,
    );
  }
}

/** `/dock/tag/graph[/<name>]` — shape check only (loaders stay near-empty). */
function loadTagRoute(dock: DockPointer): void {
  const parsed = DockPointer.parseTagPointer(dock.pointer);
  const tagName = parsed?.tag;
  if (!parsed || (tagName !== null && !isValidTag(tagName))) {
    throw new DockLoadError(
      'malformed_tag_pointer',
      'hard',
      {
        action: 'render_error',
        title: t`Tag view not found`,
        message: t`Expected /dock/tag/graph or /dock/tag/graph/<dot.tag.name>.`,
      },
      'tag',
    );
  }
}

/** `/dock/subgraph/<projection>[/<focusKey>]` — non-empty projection only. */
function loadSubgraphRoute(dock: DockPointer): void {
  if (!DockPointer.parseSubgraphPointer(dock.pointer)) {
    throw new DockLoadError(
      'malformed_subgraph_pointer',
      'hard',
      {
        action: 'render_error',
        title: t`Subgraph not found`,
        message: t`Expected /dock/subgraph/<projection>.`,
      },
      'subgraph',
    );
  }
}

export async function loadDockPointer(dock: DockPointer, context: DockLoaderContext): Promise<string> {
  // `/dock/home` is the dock spelling of the app root, and the root's canonical
  // URL is `/`. Without this redirect both spellings stay live: the address bar
  // reads `/dock/home` while the pointer parsed from it serializes to `/`, so
  // the URL silently changes under the user on their next navigation. Redirect
  // rather than render, so one location keeps exactly one address — which is the
  // point of making the root a pointer at all.
  if (dock.isRoot) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(dock.toUrl(context.requestPath));
  }

  const label = `loadDockPointer:${dock.viewType ?? 'unknown'}`;
  try {
    switch (dock.viewType) {
      case ViewType.SHELL:
      case ViewType.VIBE:
        // Step 3 ran in the dispatcher, before the tab was minted.
        await loadShellRoute(dock.pointer, context.requestPath, processRouteCarry(dock, { resolved: true }));
        break;
      case ViewType.PROJECT:
        await loadProjectRoute(dock.pointer, { viewMode: dock.viewMode });
        break;
      case ViewType.CONVERSATION:
        await loadConversationRoute(dock.pointer);
        break;
      case ViewType.AGENT:
        await loadAgentStreamInboxRoute(dock.pointer);
        break;
      case ViewType.ASSETS:
        // A concrete asset resolves its owner; only a browse landing needs a
        // remembered/default project before it can resolve anything. The
        // project landing (`project-home`) IS a browse landing — it has no
        // owner of its own — so an unscoped one restores the project too,
        // instead of rendering an empty home for no project.
        if (!dock.pointer || isProjectLandingPointer(dock.pointer) || dock.scopeProjectId) {
          await adoptScopeProject(dock);
        }
        await loadAssetRoute(dock.pointer, {
          allowLocalWikiAlias: dock.page !== PageId.HUB,
          wikiAuthority: wikiAuthorityForPage(dock.page),
          keepProject: !!dock.scopeProjectId,
        });
        break;
      case ViewType.TASKS:
        await loadTasksRoute(dock.pointer);
        break;
      case ViewType.AGENTIC_PROCESS:
        await loadAgenticProcessRoute(dock.pointer);
        break;
      case ViewType.SESSION:
        await loadSessionRoute(dock.pointer);
        break;
      case ViewType.HELPDESK:
        await loadHelpdeskRoute(dock.pointer);
        break;
      case ViewType.LIVE_SESSION:
        loadLiveSessionRoute(dock.pointer);
        break;
      // Automations: context only. The list is the mounted view's own data
      // (`useAutomations`) — a query here would repeat on every visit (dock-loading I3).
      case ViewType.AUTOMATIONS:
        await adoptScopeProject(dock);
        break;
      case ViewType.PLAN:
        await loadPlanRoute(dock.pointer);
        break;
      case ViewType.LENS:
        // Only a claude transcript owns a project (its session's). Every other
        // lens is context-neutral: the project its URL pins was already adopted
        // by the dispatcher (`main-loader`), and a claude transcript's own
        // project wins here.
        await loadLensRoute(dock.pointer);
        break;
      case ViewType.GRAPH:
        await loadGraphIdentityRoute(dock, 'graph');
        break;
      case ViewType.WORLDVIEW:
        await loadGraphIdentityRoute(dock, 'worldview');
        break;
      case ViewType.TAG:
        // URL-first + near-empty: validate shape only. NO entity resolution —
        // anonymous (ghost) tags are first-class in the tag graph.
        loadTagRoute(dock);
        break;
      case ViewType.SUBGRAPH:
        loadSubgraphRoute(dock);
        break;
      case ViewType.WEB_APP:
        // Context only, and `main-loader` already adopted it ahead of the mint.
        break;
      default:
        // Explorer, desktop, and other loader-less views: an unscoped one
        // restores the remembered project (a scoped one was adopted up front).
        await adoptScopeProject(dock);
        break;
    }
    clearDockLoadError(dock);
    return label;
  } catch (error) {
    handleDockLoadError(error, dock);
    return label;
  }
}
